import { produce } from 'immer';
import { useCallback, useEffect, useState } from 'react';
import Loader from '../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../common/widgets/LoadingSpinner';
import { InformationItem } from '../../models/Information';
import { informationService } from '../../services/information';

import { EmptyState404 } from '../Common/EmptyStates';

const NOT_FOUND_ERROR_CODE = 'errors.notfound';
const CANCELED_ERROR_CODE = 'ERR_CANCELED';

interface InformationPageState {
  details?: InformationItem | null;
  isLoading: boolean;
  hasError: boolean;
}

interface InformationPageProps {
  informationId: number;
}

export const InformationPage = ({ informationId }: InformationPageProps) => {
  const [state, setState] = useState<InformationPageState>({
    details: null,
    isLoading: true,
    hasError: false,
  });

  useEffect(() => {
    document.title = state.details?.title ?? '';
  }, [state.details?.title]);

  const loadInformation = useCallback(
    async (abortController?: AbortController) => {
      const result = await informationService.getInformation(
        Number(informationId),
        {
          signal: abortController?.signal,
        },
      );

      // A request can end up canceled for reasons other than our own
      // abortController (e.g. StrictMode's double-invoke, an extension,
      // browser-level throttling) — ignore the response either way instead
      // of treating a canceled request as "this content doesn't exist".
      if (
        abortController?.signal.aborted ||
        result.errorCode === CANCELED_ERROR_CODE
      ) {
        return;
      }

      setState(
        produce((draft: InformationPageState) => {
          draft.isLoading = false;

          if (!result.hasErrors) {
            draft.details = result.data;
            draft.hasError = false;
            return;
          }

          // Only a genuine 404 from the server means "this page doesn't
          // exist" — any other failure (timeout, 5xx, transient network
          // error) is a real error and shouldn't be shown as NotFound.
          if (result.errorCode === NOT_FOUND_ERROR_CODE) {
            draft.details = null;
            draft.hasError = false;
          } else {
            draft.hasError = true;
          }
        }),
      );
    },
    [informationId],
  );

  useEffect(() => {
    const abortController = new AbortController();

    loadInformation(abortController);

    return () => abortController.abort();
  }, [loadInformation]);

  if (!state.isLoading && !state.hasError && !state.details) {
    return <EmptyState404 />;
  }

  return (
    <section
      className={`section-main section-full information-page${
        state.isLoading ? ' information-page--loading' : ''
      }`}
    >
      <Loader
        loading={state.isLoading}
        showErrorPanel={state.hasError}
        loader={
          <div className="information-page__loader">
            <LoadingSpinner size="large" />
          </div>
        }
      >
        <div className="container">
          {state.details?.html && (
            <div
              dangerouslySetInnerHTML={{
                __html: htmlDecode(state.details.html),
              }}
            />
          )}
        </div>
      </Loader>
    </section>
  );
};

const htmlDecode = (input: string): string => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = input;

  return textarea.value;
};
